import React, { useState } from "react";
import {Avatar, Flex, Menu, Text, UnstyledButton, Button, Modal, Group} from "@mantine/core";
import {IconChevronDown, IconCreditCard, IconLogout, IconSettings, IconBug, IconMessageCircle} from "@tabler/icons-react";
import {useAuth} from "./UseAuthHook";
import { useNavigate } from "react-router-dom";

export const UserMenu: React.FC = () => {
    const { userProfile, handleLogout } = useAuth();
    const navigate = useNavigate();
    const [bugReportModalOpen, setBugReportModalOpen] = useState(false);
    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

    return (
        <>
            <Menu position="bottom-end" shadow="md" width={300}>
                <Menu.Target>
                    <UnstyledButton>
                        <Flex align="center" gap="md">
                            <Avatar
                                src={userProfile.profileUrl || null}
                                color="gray"
                                radius="xl"
                                size="md"
                            />
                            <Text className="oswald-username" size="lg">
                                {`${userProfile.firstName} ${userProfile.lastName}`.toUpperCase()}
                            </Text>
                            <IconChevronDown size="1.2rem" stroke={1.5} />
                        </Flex>
                    </UnstyledButton>
                </Menu.Target>

                <Menu.Dropdown>
                    <Menu.Label>
                        <Flex direction="column">
                            <Text size="md">{userProfile.email}</Text>
                        </Flex>
                    </Menu.Label>
                    <Menu.Divider />

                    {/* Enhanced credit display */}
                    <Flex direction="column" gap="xs" p="sm" style={{ backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
                        <Text size="md" c="black" w={700}>Credits Used:</Text>
                        <Flex justify="space-between">
                            <Text size="sm" c="black">Uploads:</Text>
                            <Text size="sm" c={userProfile.uploadCount! >= userProfile.uploadLimit! ? 'red' : 'black'}>
                                {userProfile.uploadCount} / {userProfile.uploadLimit}
                            </Text>
                        </Flex>
                        <Flex justify="space-between">
                            <Text size="sm" c="black">Downloads:</Text>
                            <Text size="sm" c={userProfile.downloadCount! >= userProfile.downloadLimit! ? 'red' : 'black'}>
                                {userProfile.downloadCount} / {userProfile.downloadLimit! >= 100 ? '∞' : userProfile.downloadLimit}
                                {/*{userProfile.downloadCount} / {userProfile.downloadLimit > 100 ? '∞' : userProfile.downloadLimit}*/}
                            </Text>
                        </Flex>
                        <Button variant="light" color="blue" size="xs" leftSection={<IconCreditCard size="1rem" />}>
                            Buy More Credits
                        </Button>
                    </Flex>

                    <Menu.Divider />

                    <Menu.Item 
                        leftSection={<IconSettings size="1rem" />}
                        onClick={() => navigate('/settings')}
                    >
                        Settings
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<IconBug size="1rem" />}
                        onClick={() => setBugReportModalOpen(true)}
                    >
                        Report Bug
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<IconMessageCircle size="1rem" />}
                        onClick={() => setFeedbackModalOpen(true)}
                    >
                        Feature Request / Feedback
                    </Menu.Item>
                    <Menu.Item
                        color="red"
                        leftSection={<IconLogout size="1rem" />}
                        onClick={handleLogout}
                    >
                        Logout
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>

            {/* Bug Report Modal */}
            <Modal 
                opened={bugReportModalOpen} 
                onClose={() => setBugReportModalOpen(false)}
                title="Submit a Bug Report"
                centered
                size="md"
            >
                <Text mb="md">
                    Thank you for helping improve Sort My Shoebox! As a token of our appreciation, we'll add <strong>100 free image credits</strong> to your account when you submit a bug report.
                </Text>
                
                <Text mb="md">
                    Please include as much detail as possible about the issue you encountered. Screenshots and step-by-step instructions are especially helpful.
                </Text>
                
                <Group justify="center" mt="xl">
                    <Button
                        onClick={() => {
                            // Close the modal
                            setBugReportModalOpen(false);
                            
                            // Prepare email with user information
                            const subject = encodeURIComponent('Bug Report!');
                            const body = encodeURIComponent(
                                'Please describe the bug you encountered:\n\n' +
                                'What page where you on/what were you doing?:\n' +
                                '1.\n' +
                                '2.\n' +
                                '3.\n\n' +
                                'Browser/Device:\n\n' +
                                '\n---\n' +
                                `Username: ${userProfile.email}\n` +
                                `Name: ${userProfile.firstName} ${userProfile.lastName}\n` +
                                '(This information will be used to add 100 free image credits to your account)'
                            );
                            
                            // Open email client
                            window.location.href = `mailto:grace@sortmyshoebox.com?subject=${subject}&body=${body}`;
                        }}
                        color="blue"
                        fullWidth
                    >
                        Submit Bug Report
                    </Button>
                </Group>
            </Modal>

            {/* Feature Request / Feedback Modal */}
            <Modal 
                opened={feedbackModalOpen} 
                onClose={() => setFeedbackModalOpen(false)}
                title="Submit Feature Request or Feedback"
                centered
                size="md"
            >
                <Text mb="md">
                    We value your input! As a token of our appreciation, we'll add <strong>50 free image credits</strong> to your account when you submit a feature request or feedback.
                </Text>
                
                <Text mb="md">
                    Your suggestions help us improve Sort My Shoebox and prioritize the features that matter most to our users.
                </Text>
                
                <Group justify="center" mt="xl">
                    <Button
                        onClick={() => {
                            // Close the modal
                            setFeedbackModalOpen(false);
                            
                            // Prepare email with user information
                            const subject = encodeURIComponent('Feature Request / Feedback');
                            const body = encodeURIComponent(
                                'Please describe your feature request or feedback:\n\n' +
                                '\n\n' +
                                'How would this feature improve your experience?\n\n' +
                                '\n\n' +
                                '\n---\n' +
                                `Username: ${userProfile.email}\n` +
                                `Name: ${userProfile.firstName} ${userProfile.lastName}\n` +
                                '(This information will be used to add 50 free image credits to your account)'
                            );
                            
                            // Open email client
                            window.location.href = `mailto:grace@sortmyshoebox.com?subject=${subject}&body=${body}`;
                        }}
                        color="blue"
                        fullWidth
                    >
                        Submit Feedback
                    </Button>
                </Group>
            </Modal>
        </>
    );
};


export const UserNamed: React.FC = () => {
    const { userProfile, handleLogout } = useAuth();
    const navigate = useNavigate();
    const [bugReportModalOpen, setBugReportModalOpen] = useState(false);
    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

    return (
        <>
            <Menu position="bottom-end" shadow="md" width={300}>
                <Menu.Target>
                    <UnstyledButton>
                        <Flex align="center" gap="md">
                            <Text className="oswald-username" size="lg">
                                {`Hello, ${userProfile.firstName}`.toUpperCase()}
                            </Text>
                            {/*<IconChevronDown size="1.2rem" stroke={1.5} />*/}
                        </Flex>
                    </UnstyledButton>
                </Menu.Target>

                <Menu.Dropdown>
                    <Menu.Label>
                        <Flex direction="column">
                            <Text size="md">{userProfile.email}</Text>
                        </Flex>
                    </Menu.Label>

                    <Menu.Divider />

                    <Menu.Item 
                        leftSection={<IconSettings size="1rem" />}
                        onClick={() => navigate('/settings')}
                    >
                        Settings
                    </Menu.Item>
                    <Menu.Item leftSection={<IconCreditCard size="1rem" />}>My Purchases</Menu.Item>
                    <Menu.Item
                        leftSection={<IconBug size="1rem" />}
                        onClick={() => setBugReportModalOpen(true)}
                    >
                        Report Bug
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<IconMessageCircle size="1rem" />}
                        onClick={() => setFeedbackModalOpen(true)}
                    >
                        Feature Request / Feedback
                    </Menu.Item>

                    <Menu.Divider />

                    <Menu.Item
                        color="red"
                        leftSection={<IconLogout size="1rem" />}
                        onClick={handleLogout}
                    >
                        Logout
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>

            {/* Bug Report Modal */}
            <Modal 
                opened={bugReportModalOpen} 
                onClose={() => setBugReportModalOpen(false)}
                title="Submit a Bug Report"
                centered
                size="md"
            >
                <Text mb="md">
                    Thank you for helping improve Sort My Shoebox! As a token of our appreciation, we'll add <strong>up to 100 free image credits</strong> to your account when you submit a bug report.
                </Text>
                
                <Text mb="md">
                    Please include as much detail as possible about the issue you encountered. Screenshots and step-by-step instructions are especially helpful.
                </Text>
                
                <Group justify="center" mt="xl">
                    <Button
                        onClick={() => {
                            // Close the modal
                            setBugReportModalOpen(false);
                            
                            // Prepare email with user information
                            const subject = encodeURIComponent('Bug Report!');
                            const body = encodeURIComponent(
                                'Please describe the bug you encountered:\n\n' +
                                'What page where you on/what were you doing?:\n' +
                                '1.\n' +
                                '2.\n' +
                                '3.\n\n' +
                                'Browser/Device:\n\n' +
                                '\n---\n' +
                                `Username: ${userProfile.email}\n` +
                                `Name: ${userProfile.firstName} ${userProfile.lastName}\n` +
                                '(This information will be used to add up to 100 free image credits to your account)'
                            );
                            
                            // Open email client
                            window.location.href = `mailto:grace@sortmyshoebox.com?subject=${subject}&body=${body}`;
                        }}
                        color="blue"
                        fullWidth
                    >
                        Submit Bug Report
                    </Button>
                </Group>
            </Modal>

            {/* Feature Request / Feedback Modal */}
            <Modal 
                opened={feedbackModalOpen} 
                onClose={() => setFeedbackModalOpen(false)}
                title="Submit Feature Request or Feedback"
                centered
                size="md"
            >
                <Text mb="md">
                    We value your input! As a token of our appreciation, we'll add <strong>up to 100 free image credits</strong> to your account when you submit a feature request or feedback.
                </Text>
                
                <Text mb="md">
                    Your suggestions help us improve Sort My Shoebox and prioritize the features that matter most to our users.
                </Text>
                
                <Group justify="center" mt="xl">
                    <Button
                        onClick={() => {
                            // Close the modal
                            setFeedbackModalOpen(false);
                            
                            // Prepare email with user information
                            const subject = encodeURIComponent('Feature Request / Feedback');
                            const body = encodeURIComponent(
                                'Please describe your feature request or feedback:\n\n' +
                                '\n\n' +
                                'How would this feature improve your experience?\n\n' +
                                '\n\n' +
                                '\n---\n' +
                                `Username: ${userProfile.email}\n` +
                                `Name: ${userProfile.firstName} ${userProfile.lastName}\n` +
                                '(This information will be used to add up to 100 free image credits to your account)'
                            );
                            
                            // Open email client
                            window.location.href = `mailto:grace@sortmyshoebox.com?subject=${subject}&body=${body}`;
                        }}
                        color="blue"
                        fullWidth
                    >
                        Submit Feedback
                    </Button>
                </Group>
            </Modal>
        </>
    );
};
